import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { fetchGeoStatus } from '../lib/api';
import { API_BASE } from '../lib/apiBase';
import GeoBlockedPage from './GeoBlockedPage';
import type { Session } from '@supabase/supabase-js';

const NEW_USER_WINDOW_MS = 5 * 60 * 1000;

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [blocked, setBlocked] = useState(false);
  // Run-once guard: a PKCE `code` is single-use, so under StrictMode's
  // double-mount (and any re-render) a second exchangeCodeForSession would fail
  // and bounce a validly-authenticated user to /login?error=auth_failed. Ensure
  // the exchange/setSession runs exactly once per mount lifecycle.
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    const handleCallback = async () => {
      // Extract 'next' redirect from query params before any navigation
      const searchParams = new URLSearchParams(window.location.search);
      const next = searchParams.get('next');
      const destination = next?.startsWith('/') ? next : '/dashboard';

      const code = searchParams.get('code');
      const hashParams = new URLSearchParams(window.location.hash.slice(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      if (code) {
        // PKCE flow: exchange code for session
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          navigate('/login?error=auth_failed', { replace: true });
          return;
        }
      } else if (accessToken && refreshToken) {
        // Magic link flow: set session directly from hash tokens
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error) {
          navigate('/login?error=auth_failed', { replace: true });
          return;
        }
      }

      // Get the established session
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        navigate('/login?error=no_session', { replace: true });
        return;
      }

      const shouldBlock = await checkGeoBlockNewUser(session);
      if (shouldBlock) {
        await supabase.auth.signOut().catch(() => {});
        setBlocked(true);
        return;
      }

      await navigateAfterAuth(session, destination, navigate);
    };

    void handleCallback();
  }, [navigate]);

  if (blocked) {
    return <GeoBlockedPage />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-[#6B7280]">Signing you in...</p>
      </div>
    </div>
  );
}

async function checkGeoBlockNewUser(session: Session): Promise<boolean> {
  try {
    const geo = await fetchGeoStatus(session.access_token);
    if (geo.isUS) return false;
    const res = await fetch(`${API_BASE}/api/me`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (!res.ok) return false;
    const data = await res.json();
    const createdAt = data?.member?.created_at;
    if (!createdAt) return false;
    const ageMs = Date.now() - new Date(createdAt).getTime();
    return ageMs < NEW_USER_WINDOW_MS;
  } catch {
    return false;
  }
}

async function fireFraudSignals(token: string): Promise<void> {
  try {
    const { collectDeviceFingerprint, collectWebRTCLeaks } = await import('../lib/deviceFingerprint');

    collectDeviceFingerprint().then(fp => {
      fetch(`${API_BASE}/api/fraud/fingerprint`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fingerprintHash: fp.hash,
          components: fp.components,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      }).catch(() => {});
    }).catch(() => {});

    collectWebRTCLeaks().then(leakedIps => {
      if (leakedIps.length > 0) {
        fetch(`${API_BASE}/api/fraud/webrtc-signal`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ leakedIps }),
        }).catch(() => {});
      }
    }).catch(() => {});
  } catch {
    // non-fatal
  }
}

async function navigateAfterAuth(
  session: Session,
  destination: string,
  navigate: (path: string, opts?: { replace?: boolean }) => void,
): Promise<void> {
  // Fire UA attribution
  try {
    const { attributeSession } = await import('../lib/ua');
    await attributeSession(session.access_token);
  } catch {
    // non-fatal
  }

  // Check onboarding status. GA4 sign_up is fired server-side when the
  // Member row is first created (see memberService + ga4Service).
  try {
    const response = await fetch(`${API_BASE}/api/me`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    if (response.ok) {
      const data = await response.json();
      // Fire fraud signals for new members (created within last 5 minutes)
      const createdAt = data?.member?.created_at;
      if (createdAt && Date.now() - new Date(createdAt).getTime() < NEW_USER_WINDOW_MS) {
        void fireFraudSignals(session.access_token);
      }
      if (data.onboarding_complete) {
        navigate(destination, { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    } else {
      navigate(destination, { replace: true });
    }
  } catch {
    navigate(destination, { replace: true });
  }
}
